import { Component, ReactNode } from 'react';
import { logClientError } from '../helpers/clientLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const CHUNK_RELOAD_KEY = 'uplife.chunkReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 60_000;

// A stale tab's lazy import 404s after a release deploy — resetting boundary
// state just retries the same dead chunk URL, so these need a page reload,
// which fetches the freshly deployed bundle (UP-3054).
export function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /loading (css )?chunk .+ failed/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message) ||
    /importing a module script failed/i.test(error.message)
  );
}

function reloadedForChunkRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
    return (
      Number.isFinite(at) && at > 0 && Date.now() - at < CHUNK_RELOAD_WINDOW_MS
    );
  } catch {
    // silent: sessionStorage unavailable — the reload-loop guard is best-effort
    return false;
  }
}

function markChunkReload(): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // silent: sessionStorage unavailable — still reload; the loop guard is best-effort
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
    logClientError(error, {
      surface: 'error-boundary',
      componentStack: info.componentStack,
      severity: isChunkLoadError(error) ? 'warn' : 'error',
    });
    if (isChunkLoadError(error) && !reloadedForChunkRecently()) {
      markChunkReload();
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="d-flex flex-column align-items-center justify-content-center p-5 text-center">
            <i
              className="bi bi-arrow-clockwise text-primary mb-3"
              style={{ fontSize: '2.5rem' }}
            />
            <h5 className="mb-2">A new version is available</h5>
            <p className="text-muted small mb-3">
              Reload the page to load the latest version of the portal.
            </p>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        );
      }
      return (
        <div className="d-flex flex-column align-items-center justify-content-center p-5 text-center">
          <i
            className="bi bi-exclamation-triangle text-danger mb-3"
            style={{ fontSize: '2.5rem' }}
          />
          <h5 className="mb-2">Something went wrong</h5>
          <p className="text-muted small mb-3">{this.state.error?.message}</p>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
